<!-- eslint-disable vue/no-template-shadow -->
<template>
  <b-modal v-model="open" :title="title" size="xl" body-class="p-2 p-md-3">
    <b-alert
      v-if="operations.data.length === 0"
      show
      variant="secondary"
      class="text-center"
    >
      Nenhum lancamento realizado para este caixa
    </b-alert>
    <b-table
      v-else
      ref="operationsList"
      class="operations-table mb-0"
      responsive
      stacked="md"
      :fields="fields"
      :items="operations.data"
      striped
      hover
      show-empty
      :busy="operations.loading"
    >
      <template #cell(name)="{ item }">
        <div class="operation-name">
          <b-badge
            v-if="isBankDeposit(item)"
            variant="info"
            class="operation-badge"
          >
            <span class="fas fa-university mr-1" />
            Deposito bancario
          </b-badge>
          <span>{{ operationName(item) }}</span>
        </div>
      </template>
      <template #cell(amount)="{ value: amount }">
        {{ amount | formatCurrency }}
      </template>
      <template #cell(type)="{ value: type }">
        {{ OPERATION_TYPES_LABELS[type] }}
      </template>
      <template #cell(receipt)="{ item }">
        <b-button
          v-if="item.receiptPhotoUrl"
          v-b-tooltip.hover
          size="sm"
          class="operation-receipt-action"
          variant="outline-primary"
          title="Ver comprovante"
          @click="openReceipt(item)"
        >
          <span class="fas fa-receipt mr-1 mr-md-0" />
          <span class="d-md-none">Ver comprovante</span>
        </b-button>
        <b-button
          v-else-if="canAttachReceipt(item)"
          v-b-tooltip.hover
          size="sm"
          class="operation-receipt-action"
          variant="outline-warning"
          title="Anexar comprovante"
          @click="openAttachReceipt(item)"
        >
          <span class="fas fa-paperclip mr-1 mr-md-0" />
          <span class="d-md-none">Anexar comprovante</span>
        </b-button>
        <span v-else class="text-muted">-</span>
      </template>
      <template #cell(createdAt)="{ value: createdAt }">
        {{ createdAt | formatDateTime }}
      </template>
      <template #cell(actions)="{ item }">
        <div v-if="canManageOperation(item)" class="operation-actions">
          <b-button
            v-b-tooltip.hover
            size="sm"
            variant="outline-secondary"
            title="Editar lancamento"
            @click="openEditOperation(item)"
          >
            <span class="fas fa-pen mr-1 mr-md-0" />
            <span class="d-md-none">Editar</span>
          </b-button>
          <b-button
            v-b-tooltip.hover
            size="sm"
            variant="outline-danger"
            title="Excluir lancamento"
            @click="deleteOperation(item)"
          >
            <span class="fas fa-trash mr-1 mr-md-0" />
            <span class="d-md-none">Excluir</span>
          </b-button>
        </div>
        <span v-else class="text-muted">-</span>
      </template>
    </b-table>

    <AddOperationModal
      v-if="editModalOpen && selectedOperation"
      :key="selectedOperation.id"
      v-model="editModalOpen"
      :checkout="checkout"
      :type="selectedOperation.type"
      :operation="selectedOperation"
      @ok="updateOperation"
      @cancel="closeEditOperation"
    />

    <b-modal
      v-model="receiptModal.visible"
      title="Comprovante da saida"
      size="lg"
      hide-footer
    >
      <img
        v-if="receiptModal.url"
        :src="receiptModal.url"
        alt="Comprovante da saida"
        class="receipt-image"
      />
    </b-modal>

    <DepositReceiptModal
      v-if="attachReceiptModalOpen"
      v-model="attachReceiptModalOpen"
      @ok="attachReceipt"
      @cancel="closeAttachReceipt"
    />

    <template #modal-footer="{ close }">
      <b-button variant="outline-secondary" @click="close"> Fechar </b-button>
    </template>
  </b-modal>
</template>

<script>
import {
  OPERATION_CATEGORIES,
  OPERATION_NAMES_LABEL,
  OPERATION_TYPES,
  OPERATION_TYPES_LABELS
} from '@/constants'
import { mapGetters } from 'vuex'
import withAsyncAction from '@/mixins/withAsyncAction'
import services from '@/services'
import { formatDateTime } from '@/helpers/date'
import { formatCurrency } from '@/helpers/numbers'
import AddOperationModal from './AddOperationModal'
import DepositReceiptModal from './DepositReceiptModal'

const EDIT_WINDOW_IN_MINUTES = 5

export default {
  name: 'OperationsModal',
  components: {
    AddOperationModal,
    DepositReceiptModal
  },
  filters: {
    formatDateTime,
    formatCurrency
  },
  mixins: [
    withAsyncAction({
      key: 'operations',
      initialState: [],
      fetcher: {
        methodName: 'fetchOperations',
        handler: services.operations.fetchOperations
      }
    })
  ],
  props: {
    value: {
      type: Boolean
    },
    type: {
      type: Array,
      default: () => []
    },
    checkout: {
      type: Object,
      required: true
    },
    date: {
      type: String,
      default: null
    },
    readonly: {
      type: Boolean
    },
    isAdmin: {
      type: Boolean
    }
  },
  data() {
    return {
      editModalOpen: false,
      selectedOperation: null,
      receiptModal: {
        visible: false,
        url: null
      },
      attachReceiptModalOpen: false,
      receiptOperation: null
    }
  },
  computed: {
    ...mapGetters('user', ['currentUserId']),
    open: {
      set(value) {
        this.$emit('input', value)
      },
      get() {
        return this.value
      }
    },
    title() {
      if (this.type.length > 1) {
        return 'Lancamentos'
      }

      return this.type.includes(OPERATION_TYPES.REVENUE) ? 'Entradas' : 'Saidas'
    },
    fields() {
      return [
        {
          key: 'name',
          label: 'Descricao'
        },
        {
          key: 'amount',
          label: 'Valor'
        },
        {
          key: 'type',
          label: 'Tipo'
        },
        {
          key: 'receipt',
          label: 'Recibo'
        },
        {
          key: 'createdAt',
          label: 'Lancado em'
        },
        {
          key: 'actions',
          label: 'Acoes'
        }
      ]
    }
  },
  watch: {
    async open(value) {
      if (value) {
        await this.loadOperations()
      }
    },
    editModalOpen(value) {
      if (!value) {
        this.selectedOperation = null
      }
    }
  },
  created() {
    this.OPERATION_TYPES_LABELS = OPERATION_TYPES_LABELS
    this.OPERATION_NAMES_LABEL = OPERATION_NAMES_LABEL
  },
  methods: {
    async loadOperations() {
      const {
        checkout: { id },
        type
      } = this

      const isOnlyRevenue =
        type.length === 1 && type.includes(OPERATION_TYPES.REVENUE)

      await this.fetchOperations({
        checkoutId: id,
        type,
        ...(isOnlyRevenue && { paymentId: null })
      })
    },
    isBankDeposit(operation) {
      return operation.category === OPERATION_CATEGORIES.BANK_DEPOSIT
    },
    canAttachReceipt(operation) {
      return operation.type === OPERATION_TYPES.EXPENSE && !operation.paymentId
    },
    operationName(operation) {
      if (this.isBankDeposit(operation) && !operation.name) {
        return 'Deposito bancario'
      }

      return OPERATION_NAMES_LABEL[operation.name] || operation.name
    },
    canManageOperation(operation) {
      if (operation.paymentId) return false
      if (this.isAdmin) return true
      if (this.readonly) return false
      if (Number(operation.createdBy) !== Number(this.currentUserId)) {
        return false
      }

      const createdAt = new Date(operation.createdAt).getTime()
      const elapsedInMinutes = (Date.now() - createdAt) / 60000
      return elapsedInMinutes <= EDIT_WINDOW_IN_MINUTES
    },
    openReceipt(operation) {
      this.receiptModal = {
        visible: true,
        url: operation.receiptPhotoUrl
      }
    },
    openAttachReceipt(operation) {
      this.receiptOperation = operation
      this.attachReceiptModalOpen = true
    },
    closeAttachReceipt() {
      this.receiptOperation = null
      this.attachReceiptModalOpen = false
    },
    async attachReceipt({ receiptPhoto }) {
      await services.operations.attachReceipt({
        id: this.receiptOperation.id,
        receiptPhoto
      })
      this.$root.$toast.success('Comprovante anexado com sucesso')
      this.closeAttachReceipt()
      await this.loadOperations()
      this.$emit('refresh')
    },
    openEditOperation(operation) {
      this.selectedOperation = { ...operation }
      this.$nextTick(() => {
        this.editModalOpen = true
      })
    },
    closeEditOperation() {
      this.selectedOperation = null
      this.editModalOpen = false
    },
    async updateOperation(operation) {
      await services.operations.updateOperation(operation)
      this.$root.$toast.success('Lancamento atualizado com sucesso')
      this.closeEditOperation()
      await this.loadOperations()
      this.$emit('refresh')
    },
    async deleteOperation(operation) {
      const result = await this.$bvModal.msgBoxConfirm(
        'Deseja realmente excluir este lancamento?',
        {
          title: 'Excluir lancamento',
          okTitle: 'Excluir',
          okVariant: 'danger',
          cancelTitle: 'Cancelar',
          centered: true
        }
      )

      if (!result) return

      await services.operations.deleteOperation(operation.id)
      this.$root.$toast.success('Lancamento excluido com sucesso')
      await this.loadOperations()
      this.$emit('refresh')
    }
  }
}
</script>

<style scoped>
.operation-name {
  align-items: flex-start;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  min-width: 0;
  overflow-wrap: anywhere;
}

.operation-badge {
  font-size: 0.72rem;
}

.operation-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
}

.receipt-image {
  display: block;
  max-height: 70vh;
  object-fit: contain;
  width: 100%;
}

@media (max-width: 767.98px) {
  .operations-table {
    max-width: 100%;
    min-width: 0;
    overflow-x: hidden;
    width: 100%;
  }

  .operations-table ::v-deep .table {
    margin-bottom: 0;
    width: 100%;
  }

  .operations-table ::v-deep tbody tr {
    border: 1px solid #dee2e6;
    border-radius: 0.35rem;
    margin-bottom: 0.75rem;
    overflow: hidden;
  }

  .operations-table ::v-deep tbody tr:last-child {
    margin-bottom: 0;
  }

  .operations-table ::v-deep td,
  .operations-table ::v-deep td > div {
    min-width: 0;
    overflow-wrap: anywhere;
  }

  .operation-actions,
  .operation-receipt-action {
    width: 100%;
  }

  .operation-actions .btn {
    flex: 1 1 0;
  }
}
</style>
